import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ScrollReveal from "@/components/ScrollReveal";
import heroCows from "@/assets/hero-cows-new.jpg";
import boumaticLogo from "@/assets/boumatic-logo.jpg";
import muellerLogo from "@/assets/mueller-logo-new.png";
import liberchemLogo from "@/assets/liberchem-logo.png";
import lelyLogo from "@/assets/lely-logo.png";
import agroLogo from "@/assets/agro-logo.svg";
import lelyAstronautOccasion from "@/assets/lely-astronaut-occasion.png";
import assistanceTraite from "@/assets/assistance-traite.jpg";
import detectionFuites from "@/assets/detection-fuites.jpg";
import inspectionTraite from "@/assets/inspection-traite.jpg";
import proActionImg from "@/assets/pro-action.png";
import urgenceImg from "@/assets/urgence-247.jpg";
import { memo } from "react";

const Home = memo(() => {
  return (
    <>
      <Helmet>
        <title>Agro-Réfrigération - Spécialiste en équipements robotisés et traite laitière</title>
        <meta 
          name="description" 
          content="Fournisseur de solutions innovantes et performantes pour optimiser les opérations des fermes laitières du Centre-du-Québec, de l'Estrie et de Lotbinière." 
        />
        <link rel="preload" as="image" href={heroCows} />
      </Helmet>

      <div className="space-y-16">
        {/* Hero Section */}
        <section className="relative h-[600px] -mt-16 flex">
          {/* Image à gauche */}
          <div className="w-full lg:w-[55%] relative overflow-hidden">
            <img 
              src={heroCows} 
              alt="Vaches laitières" 
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </div>
          
          {/* Contenu à droite */}
          <div className="w-full lg:w-[45%] bg-[#2c2c2c] flex items-center justify-center px-8 lg:px-12">
            <div className="max-w-xl text-white space-y-6">
              <p className="text-sm uppercase tracking-wider font-semibold">AGRO</p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                Votre spécialiste en équipements robotisés et traite laitière
              </h1>
              <p className="text-base md:text-lg leading-relaxed">
                Fournisseur de solutions innovantes et performantes pour optimiser les opérations 
                des fermes laitières du Centre-du-Québec, de l'Estrie et de Lotbinière.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-white">
                  <Link to="/boutique">Découvrez nos produits</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-white hover:bg-gray-100 text-gray-900 border-white">
                  <Link to="/services">Voir nos services</Link>
                </Button>
              </div>
              
              {/* Google Reviews */}
              <div className="pt-8 flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">G</span>
                </div>
                <div>
                  <h3 className="font-semibold text-base">Agro-Réfrigération inc</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xl font-bold text-yellow-400">4.5</span>
                    <div className="flex text-yellow-400 text-sm">
                      {"★★★★☆".split("").map((star, i) => (
                        <span key={i}>{star}</span>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Basé sur 14 avis</p>
                  <p className="text-xs text-gray-500 mt-1">powered by <span className="font-semibold">Google</span></p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Notre Histoire */}
        <ScrollReveal>
          <section className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-4">
                <p className="text-sm uppercase tracking-wider text-accent font-semibold">Notre Histoire</p>
                <h2 className="text-3xl md:text-4xl font-bold">Découvrez Agro</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Agro a été fondée en octobre 1988 par Messieurs Normand Tremblay et Réjean Houle, 
                  deux entrepreneurs passionnés par l'agriculture et l'industrie laitière. Au fil des ans, 
                  l'entreprise a grandi pour devenir un des fournisseurs clés de solutions pour les fermes 
                  laitières dans la région du Centre-du-Québec, de l'Estrie et de Lotbinière.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Nos clients sont au cœur de notre mission. Nous nous attachons à comprendre leurs besoins 
                  uniques, à innover pour y répondre et à offrir un service irréprochable. Notre ambition? 
                  Se positionner en leaders de notre domaine, et cela commence ici, avec vous.
                </p>
                <Button asChild variant="outline">
                  <Link to="/a-propos">En savoir plus</Link>
                </Button>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-muted/50 to-muted flex items-center justify-center aspect-[4/3]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,hsl(var(--accent)/0.08),transparent_60%)]" />
                <div className="relative z-10 flex flex-col items-center gap-6 p-10">
                  <img 
                    src={agroLogo} 
                    alt="Logo Agro-Réfrigération"
                    className="w-full max-w-xs h-auto drop-shadow-md"
                    loading="lazy"
                  />
                  <div className="w-16 h-1 rounded-full bg-accent/60" />
                  <p className="text-sm text-muted-foreground font-medium tracking-wide uppercase">Depuis 1988</p>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Nos Produits Phares */}
        <ScrollReveal delay={0.2}>
          <section className="bg-[#2c2c2c] py-16">
            <div className="container mx-auto px-4">
              {/* Top section: Texte à gauche + Carte LELY à droite */}
              <div className="grid lg:grid-cols-2 gap-8 mb-12">
                {/* Texte sur fond sombre */}
                <div className="text-white space-y-6 flex flex-col justify-center">
                  <div>
                    <div className="w-12 h-1 bg-accent mb-4"></div>
                    <p className="text-sm uppercase tracking-wider font-semibold mb-2">SOLUTIONS INNOVANTES</p>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold">Nos produits phares</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Chez Agro, nous mettons à votre disposition une gamme complète de produits de haute qualité, 
                    spécialement conçus pour répondre aux besoins uniques des fermes laitières. Découvrez nos produits phares.
                  </p>
                </div>

                {/* Carte LELY Featured */}
                <div className="bg-white rounded-lg p-8 shadow-xl">
                  <h3 className="font-bold text-xl mb-6 text-center">Vivez la vie LELY</h3>
                  <div className="flex items-center justify-center mb-6">
                    <img 
                      src={lelyLogo} 
                      alt="LELY"
                      className="h-24 w-auto"
                    />
                  </div>
                  <p className="text-center text-muted-foreground mb-6 leading-relaxed">
                    Leader reconnu dans l'Est-du-Québec, LELY se distingue comme une référence dans le domaine 
                    des robots de traite, offrant des solutions d'automatisation avancées pour optimiser le 
                    rendement des fermes laitières.
                  </p>
                  <div className="flex justify-center">
                    <Button className="bg-[#2c2c2c] hover:bg-[#3c3c3c] text-white">
                      En savoir plus
                    </Button>
                  </div>
                </div>
              </div>

              {/* 3 autres produits en bas */}
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
                  <h3 className="font-semibold text-lg mb-4 text-center">Pour la vie de votre laiterie</h3>
                  <div className="h-20 flex items-center justify-center mb-4">
                    <img 
                      src={boumaticLogo} 
                      alt="BouMatic"
                      className="h-16 w-auto object-contain mx-auto"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    Chez Agro, nous sommes fiers de vous fournir la gamme de produits BouMatic depuis 1993, 
                    conçus pour harmoniser passion et efficacité.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
                  <h3 className="font-semibold text-lg mb-4 text-center">Services et innovation</h3>
                  <div className="h-20 flex items-center justify-center mb-4">
                    <img 
                      src={muellerLogo} 
                      alt="Mueller"
                      className="h-16 w-auto object-contain mx-auto"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    En partenariat avec Mueller, Agro apporte une dimension innovante à l'industrie laitière.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
                  <h3 className="font-semibold text-lg mb-4 text-center">Produits de laboratoire</h3>
                  <div className="h-20 flex items-center justify-center mb-4">
                    <img 
                      src={liberchemLogo} 
                      alt="Liberchem"
                      className="h-16 w-auto object-contain mx-auto"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    Liberchem, un fabricant québécois basé à Dorval et spécialisé dans les produits sanitaires 
                    et d'hygiène du pis de vache, contribue à l'amélioration de vos techniques de traite.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Produits d'occasion */}
        <ScrollReveal delay={0.3}>
          <section className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src={lelyAstronautOccasion}
                  alt="Produits d'occasion"
                  className="w-full h-auto"
                />
              </div>
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold">Produits d'occasion – Valeur garantie</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Avec Agro, maximisez vos performances sans compromettre votre budget. Nos équipements 
                  d'occasion en traite, testés et certifiés, vous assurent une efficacité optimale. 
                  Choisissez la qualité et l'économie pour propulser votre exploitation à son potentiel 
                  maximum. Parce que chez Agro, votre réussite est notre priorité.
                </p>
                <Button asChild>
                  <Link to="/boutique">Voir les produits disponibles</Link>
                </Button>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Services sur mesure */}
        <ScrollReveal delay={0.4}>
          <section className="bg-gray-100 py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <p className="text-sm uppercase tracking-wider text-gray-600 font-semibold mb-2">SERVICES SUR MESURE</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Des services adaptés à vos besoins</h2>
              <p className="text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                Chez Agro, nous comprenons que chaque ferme laitière a des besoins spécifiques. C&apos;est pourquoi nous offrons 
                une gamme de services sur mesure, conçus pour optimiser votre production laitière et assurer le bien-être de 
                vos vaches. De l&apos;assistance à la traite à la détection de fuites, nous sommes là pour vous aider à maximiser 
                l&apos;efficacité de votre exploitation.
              </p>
            </div>

            <Tabs defaultValue="assistance" className="w-full">
              <TabsList className="flex w-full h-auto bg-transparent border-b rounded-none mb-8 overflow-x-auto scrollbar-none">
                <TabsTrigger 
                  value="assistance" 
                  className="data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none pb-3 whitespace-nowrap flex-shrink-0"
                >
                  Assistance à la traite
                </TabsTrigger>
                <TabsTrigger 
                  value="fuites"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none pb-3 whitespace-nowrap flex-shrink-0"
                >
                  Détection et réparation de fuites
                </TabsTrigger>
                <TabsTrigger 
                  value="inspection"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none pb-3 whitespace-nowrap flex-shrink-0"
                >
                  Inspection du système de traite
                </TabsTrigger>
                <TabsTrigger 
                  value="proaction"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none pb-3 whitespace-nowrap flex-shrink-0"
                >
                  Rapports Pro Action
                </TabsTrigger>
                <TabsTrigger 
                  value="urgence"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none pb-3 whitespace-nowrap flex-shrink-0"
                >
                  Urgence 24/7
                </TabsTrigger>
              </TabsList>

              <TabsContent value="assistance" className="mt-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={assistanceTraite}
                      alt="Assistance à la traite"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-2xl md:text-3xl font-bold">Assistance à la traite</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Un service qui vise à optimiser votre système de traite, améliorer la qualité du lait et le bien-être 
                      de vos vaches. Notre expertise s&apos;appuie sur des années d&apos;expérience dans le soutien des fermes laitières. 
                      En plus de maintenir des conditions sanitaires optimales, nous fournissons une inspection du système de 
                      traite pour détecter rapidement les problèmes et y remédier.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Nos équipes techniques sont à votre disposition pour une formation efficace et pour garantir la salubrité 
                      du lait produit. Avec Agro, bénéficiez d&apos;un service d&apos;assistance à la traite exceptionnel et contribuez 
                      à un meilleur bien-être pour vos vaches laitières.
                    </p>
                    <Button className="bg-[#2c2c2c] hover:bg-[#3c3c3c] text-white">
                      Découvrir notre service
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="fuites" className="mt-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={detectionFuites}
                      alt="Détection et réparation de fuites"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-2xl md:text-3xl font-bold">Détection, réparation de fuites</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Les fuites dans les systèmes de refroidissement sont un problème récurrent qui peut impacter l&apos;efficacité 
                      et la durée de vie de vos équipements. Chez Agro, nous employons des technologies avancées pour localiser 
                      rapidement ces fuites.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Notre approche consiste à travailler étroitement avec nos clients pour concevoir une solution de réparation 
                      adaptée à leurs besoins spécifiques. Le résultat ? Une efficacité énergétique améliorée, une durée de vie 
                      prolongée de votre système, et des économies sur les coûts de réparation à long terme.
                    </p>
                    <Button className="bg-[#2c2c2c] hover:bg-[#3c3c3c] text-white">
                      Découvrir notre service
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="inspection" className="mt-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={inspectionTraite}
                      alt="Inspection du système de traite"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-2xl md:text-3xl font-bold">Inspection du système de traite</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      La qualité du lait est déterminée par une multitude de facteurs, notamment le système de traite. Chez Agro, 
                      nous comprenons l&apos;importance d&apos;un système de traite performant et fiable pour la qualité du lait.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Notre service d&apos;inspection du système de traite, réalisé par des experts qualifiés, assure une évaluation 
                      complète pour détecter tout problème potentiel. Le résultat est un lait de qualité supérieure, une réduction 
                      des risques de maladie pour vos vaches laitières, et une diminution des coûts de maintenance.
                    </p>
                    <Button className="bg-[#2c2c2c] hover:bg-[#3c3c3c] text-white">
                      Découvrir notre service
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="proaction" className="mt-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={proActionImg}
                      alt="Pro Action"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-2xl md:text-3xl font-bold">Rapports Pro Action</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Le moindre détail compte en production laitière. Les rapports Pro Action reflètent l&apos;état de santé de 
                      votre ferme, fournissant une analyse de la composition du lait et des informations sur le bien-être de 
                      vos vaches.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Notre service de rapports Pro Action déchiffre ces données complexes pour vous. Grâce à nos analyses de 
                      laboratoire rigoureuses et à nos recommandations personnalisées, optimisez votre productivité et rentabilité.
                    </p>
                    <Button className="bg-[#2c2c2c] hover:bg-[#3c3c3c] text-white">
                      Découvrir notre service
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="urgence" className="mt-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={urgenceImg}
                      alt="Urgence 24/7"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-2xl md:text-3xl font-bold">Urgence 24/7 – Une assistance rapide et fiable en tout temps</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Faites face aux imprévus sans stress grâce à notre Service d&apos;urgence 24/7. Disponible même en dehors des 
                      heures d&apos;ouverture traditionnelles, notre équipe d&apos;experts est toujours prête à intervenir. Dans les situations 
                      critiques où chaque minute compte, nous nous engageons à fournir une réponse rapide et efficace.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Notre Service d&apos;urgence 24/7 offre une assistance constante, jour et nuit. Il comprend un support téléphonique 
                      en temps réel et des interventions sur site si nécessaire. Diagnostics et résolutions rapides vous permettent 
                      de reprendre vos activités sans tarder.
                    </p>
                    <Button className="bg-[#2c2c2c] hover:bg-[#3c3c3c] text-white">
                      Découvrir notre service
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          </section>
        </ScrollReveal>

        {/* CTA Final */}
        <ScrollReveal delay={0.5}>
          <section className="bg-[#2c2c2c] text-white py-16">
            <div className="container mx-auto px-4 text-center">
              <p className="text-sm uppercase tracking-wider mb-4">CONTACTEZ-NOUS</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Prêt à Optimiser Votre Production Laitière?
              </h2>
              <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
                Exploitez pleinement le potentiel de votre production laitière avec Agro. Contactez-nous dès aujourd&apos;hui pour découvrir comment
              </p>
              <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
                nos services et produits peuvent aider à améliorer l&apos;efficacité de votre exploitation et garantir la meilleure qualité de lait possible.
              </p>
              <p className="text-base md:text-lg mb-8 max-w-4xl mx-auto leading-relaxed">
                Nous sommes disponibles 24/7 pour vous assister dans vos urgences.
              </p>
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-white">
                <Link to="/contact">Contactez-nous dès maintenant!</Link>
              </Button>
            </div>
          </section>
        </ScrollReveal>
      </div>
    </>
  );
});

Home.displayName = "Home";

export default Home;
